import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ArrayTableView from './ArrayTableView';
import { isArrayTableViewable } from '../utils/treeBuilder';

/**
 * 获取值的颜色（支持深色模式）
 * @param {RenderNode} node - 渲染节点
 * @returns {string} Tailwind 颜色类
 */
function getValueColor(node) {
  if (node.type === 'primitive') {
    if (node.value === null) return 'text-gray-400 dark:text-gray-500';
    const type = typeof node.value;
    if (type === 'string') return 'text-green-600 dark:text-green-400';
    if (type === 'number') return 'text-blue-600 dark:text-blue-400';
    if (type === 'boolean') return 'text-orange-600 dark:text-orange-400';
  }
  if (node.type === 'object') return 'text-purple-600 dark:text-purple-400';
  if (node.type === 'array') return 'text-indigo-600 dark:text-indigo-400';
  return 'text-gray-800 dark:text-gray-200';
}

/**
 * 表格行组件（递归）
 * @param {Object} props
 * @param {RenderNode} props.node - 当前节点
 * @param {Function} props.onToggle - 展开/折叠回调
 */
function TableRow({ node, onToggle }) {
  return (
    <div>
      {/* 当前行 */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-150">
        {/* Key 列 */}
        <div
          className="w-1/2 px-6 py-3 border-r-2 border-gray-200 dark:border-gray-700 flex items-center"
          style={{ paddingLeft: `${node.level * 24 + 24}px` }}
        >
          {/* 展开/折叠图标 */}
          {node.isExpandable && (
            <button
              onClick={() => onToggle(node.id)}
              className="mr-2 p-1 hover:bg-indigo-100 dark:hover:bg-indigo-900 rounded-md transition-colors duration-150 text-indigo-600 dark:text-indigo-400"
            >
              {node.isExpanded ? (
                <ChevronDown size={18} />
              ) : (
                <ChevronRight size={18} />
              )}
            </button>
          )}

          {/* 不可展开的占位 */}
          {!node.isExpandable && <span className="w-6 mr-2" />}

          {/* 键名 */}
          <span className="font-mono text-sm font-medium text-gray-800 dark:text-gray-200">{node.key}</span>
        </div>

        {/* Value 列 */}
        <div className="w-1/2 px-6 py-3 font-mono text-sm">
          <span className={`${getValueColor(node)} font-medium`}>{node.displayValue}</span>
        </div>
      </div>

      {/* 递归渲染子节点或表格视图 */}
      {node.isExpanded && node.children && (
        <>
          {/* 如果是数组且适合表格视图，使用表格展示 */}
          {node.type === 'array' && isArrayTableViewable(node.value) ? (
            <ArrayTableView data={node.value} level={node.level} />
          ) : (
            /* 否则递归渲染子节点 */
            node.children.map((child) => (
              <TableRow key={child.id} node={child} onToggle={onToggle} />
            ))
          )}
        </>
      )}
    </div>
  );
}

export default React.memo(TableRow);
